/**
 * The client's own progress view (#232).
 *
 * Pure geometry and aggregation. The chart components import this and render;
 * anything that can quietly draw a trend the client did not actually have —
 * scaling, path building across a missing weigh-in, deciding whether a change
 * is an improvement — lives here where it can be tested without a DOM.
 *
 * The direction table, the weight parsing and the null-run splitting all come
 * from @light2live/shared-types, so this view and the coach's comparison sheet
 * cannot disagree about whether a week went well.
 */

import {
    ALL_FREQUENCY_ROWS,
    changeIn,
    concernOfFrequency,
    countHighIn,
    DIFFICULT_FEELING_ROWS,
    parseWeightKg,
    PHYSICAL_SYMPTOM_ROWS,
    POSITIVE_FEELING_ROWS,
    segmentSeries,
    type Concern,
    type Direction,
    type FrequencyRow,
    type MetricChange,
    type ProgressReport,
    type ScaleField,
    type TrendDirection,
} from '@light2live/shared-types';

/**
 * The direction table, the change arithmetic and the verdict all come from the
 * shared package, so this view and the coach's sheet cannot disagree about
 * whether a week went well. Re-exported here because the components in this app
 * import them from '@/lib/progress-trend'.
 *
 * `TrendDirection` covers weight's 'neutral', which is deliberately directionless:
 * see `METRICS` below.
 */
export { ALL_FREQUENCY_ROWS, verdictOf } from '@light2live/shared-types';
export type { TrendDirection, Verdict } from '@light2live/shared-types';

export type TrendPoint = {
    /**
     * Column position, 0-based. The x axis, and — as `index + 1` — the report's
     * display ordinal. Derived from the ordered list rather than stored, so it
     * cannot disagree with the dates.
     */
    index: number;
    reportDate: string;
    /** Null means the client did not record this one. Not zero, not skipped. */
    value: number | null;
};

export type TrendMetric = {
    key: string;
    label: string;
    unit: string;
    direction: TrendDirection;
    /** Fixed axis, or null to scale to the data. */
    fixedDomain: { lo: number; hi: number } | null;
    format: (value: number) => string;
    /**
     * How to read this metric off one report. Null means the client did not
     * record it — never zero.
     *
     * A function rather than a field name because half of these are not fields:
     * weight is a Decimal string that needs parsing, and the two feeling
     * metrics are counts across a group of rows. Switching on `key` inside
     * `seriesFor` is what this replaces, and that switch was already one branch
     * away from silently reading `report['difficultFeelings']` as undefined.
     */
    valueOf: (report: ProgressReport) => number | null;
};

/**
 * Weight.
 *
 * Direction is 'neutral' on purpose. A client may be trying to gain, maintain
 * or lose, and the app does not know which, so it shows the number and the
 * change and does not colour it as good or bad news. It has no natural range so
 * the axis scales to the data, with padding.
 */
const WEIGHT: TrendMetric = {
    key: 'weight',
    label: 'Weight',
    unit: 'kg',
    direction: 'neutral',
    fixedDomain: null,
    format: (value) => value.toFixed(1),
    valueOf: (report) => parseWeightKg(report.weightKg),
};

/**
 * The 1-10 scales get a FIXED axis rather than one scaled to the data. Scaling
 * them would turn a wobble between 6 and 7 into a chart that swings corner to
 * corner, which reads as a dramatic month when it was a flat one.
 */
function scaleMetric(
    key: ScaleField,
    label: string,
    direction: TrendDirection,
    max: number,
): TrendMetric {
    return {
        key,
        label,
        unit: `out of ${max}`,
        direction,
        fixedDomain: { lo: 1, hi: max },
        format: (value) => String(value),
        valueOf: (report) => toFiniteOrNull(report[key]),
    };
}

const ENERGY = scaleMetric('energyLevel', 'Energy', 'higher-is-better', 10);
const SLEEP = scaleMetric('sleepQuality', 'Sleep quality', 'higher-is-better', 10);
const PAIN = scaleMetric('painLevel', 'Pain level', 'higher-is-worse', 10);

/**
 * A count of rows answered Often or Always, as a metric.
 *
 * The direction is the ROWS' direction, not a choice made here: over the
 * difficult feelings a rising count is bad news, and over the positive ones the
 * identical arithmetic is good news. `countHighIn` requires the group to be
 * homogeneous, which is why both groups are filtered on direction rather than
 * hand-listed.
 *
 * The axis is fixed to the group size so a client with three difficult feelings
 * out of three does not get the same picture as three out of five.
 */
function countMetric(key: string, label: string, rows: readonly FrequencyRow[]): TrendMetric {
    return {
        key,
        label,
        unit: `of ${rows.length}`,
        direction: rows[0].direction,
        fixedDomain: { lo: 0, hi: rows.length },
        format: (value) => String(value),
        // Never null: a filed report has an answer in every row, so unlike a
        // weigh-in there is no such thing as a missing count.
        valueOf: (report) => countHighIn(report, rows),
    };
}

const DIFFICULT_FEELINGS = countMetric(
    'difficultFeelings',
    'Difficult feelings',
    DIFFICULT_FEELING_ROWS,
);
const POSITIVE_FEELINGS = countMetric(
    'positiveFeelings',
    'Positive feelings',
    POSITIVE_FEELING_ROWS,
);
const PHYSICAL_SYMPTOMS = countMetric(
    'physicalSymptoms',
    'Physical symptoms',
    PHYSICAL_SYMPTOM_ROWS,
);

/**
 * The three the headline block opens with. Order matters: the tests index this.
 */
export const METRICS: readonly TrendMetric[] = [WEIGHT, ENERGY, SLEEP];

export type MetricGroup = {
    key: 'body' | 'energy-sleep' | 'mood';
    label: string;
    /** One line under the heading, so a group is never a bare word. */
    blurb: string;
    metrics: readonly TrendMetric[];
};

/**
 * The page's hierarchy (#241 part B).
 *
 * Three groups instead of one flat divided list, because "weight, energy,
 * sleep quality" in a row asks the reader to hold three unrelated scales at
 * once. Grouped, each card answers one question.
 *
 * Every metric here is composed from the consts above, so the headline block
 * and this list cannot drift into describing the same reading differently.
 */
export const METRIC_GROUPS: readonly MetricGroup[] = [
    {
        key: 'body',
        label: 'Body',
        blurb: 'Weight, pain and the physical symptoms you have reported.',
        metrics: [WEIGHT, PAIN, PHYSICAL_SYMPTOMS],
    },
    {
        key: 'energy-sleep',
        label: 'Energy and sleep',
        blurb: 'How much you have had to work with, and how well you have rested.',
        metrics: [ENERGY, SLEEP],
    },
    {
        key: 'mood',
        label: 'Mood',
        blurb: 'The feelings you reported as Often or Always, both kinds.',
        metrics: [DIFFICULT_FEELINGS, POSITIVE_FEELINGS],
    },
];

/**
 * One metric's series across the client's reports.
 *
 * Anything non-finite becomes null so it is drawn as a gap rather than as a
 * point at NaN.
 */
export function seriesFor(reports: readonly ProgressReport[], metric: TrendMetric): TrendPoint[] {
    return reports.map((report, index) => ({
        index,
        reportDate: report.reportDate,
        value: metric.valueOf(report),
    }));
}

function toFiniteOrNull(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

// ---------------------------------------------------------------------------
// Geometry
// ---------------------------------------------------------------------------

export type ChartGeometry = {
    width: number;
    height: number;
    pad: { top: number; right: number; bottom: number; left: number };
};

/**
 * A fixed coordinate system scaled by CSS. The same markup is legible on a
 * phone and on a desktop without recomputing anything on resize.
 */
export const CHART: ChartGeometry = {
    width: 320,
    height: 120,
    pad: { top: 18, right: 12, bottom: 22, left: 12 },
};

/**
 * The compact form used inside a metric row (#241 part B).
 *
 * Same maths, smaller frame — the padding exists only to keep a 2px stroke and
 * a 2.5px dot from being clipped at the edges, because a sparkline carries no
 * labels for padding to make room for. Deliberately the SAME functions as the
 * full chart rather than a second implementation: `linePaths` is the one thing
 * standing between a missing weigh-in and #278, and it is not being forked.
 */
export const SPARK: ChartGeometry = {
    width: 120,
    height: 32,
    pad: { top: 4, right: 4, bottom: 4, left: 4 },
};

export type Domain = { lo: number; hi: number };

/**
 * The value range to draw across.
 *
 * A metric with a fixed axis uses it. Otherwise the data's own range with 15%
 * padding, and at least half a unit, so a client whose weight barely moves gets
 * a near-flat line through the middle rather than one pinned to the frame.
 */
export function domainFor(points: readonly TrendPoint[], metric: TrendMetric): Domain {
    if (metric.fixedDomain) return metric.fixedDomain;

    const values = points
        .map((point) => point.value)
        .filter((value): value is number => value !== null);

    if (values.length === 0) return { lo: 0, hi: 1 };

    const min = Math.min(...values);
    const max = Math.max(...values);
    const pad = Math.max((max - min) * 0.15, 0.5);

    return { lo: min - pad, hi: max + pad };
}

export function scaleX(index: number, count: number, chart: ChartGeometry = CHART): number {
    const inner = chart.width - chart.pad.left - chart.pad.right;
    if (count <= 1) return chart.pad.left + inner / 2;
    return chart.pad.left + (index / (count - 1)) * inner;
}

export function scaleY(value: number, domain: Domain, chart: ChartGeometry = CHART): number {
    const inner = chart.height - chart.pad.top - chart.pad.bottom;
    if (domain.hi === domain.lo) return chart.pad.top + inner / 2;
    return chart.pad.top + (1 - (value - domain.lo) / (domain.hi - domain.lo)) * inner;
}

/**
 * The `d` attributes to draw, one per contiguous run of recorded values.
 *
 * The naive version of this — map every point, join with 'L' — is #278, and it
 * fails without any error at all. Measured on [160, null, 158, 157] it produced
 *
 *     M12,28 L110.7,3228 L209.3,68 L308,88
 *
 * because `null` coerces to 0 in arithmetic, so the missing weigh-in scaled to
 * y=3228 in a 120-unit viewBox. The path is valid SVG, so nothing complains and
 * nothing truncates; the client just sees a line plunge off the bottom of the
 * chart and come back. The Svelte version in #278 coerced to NaN instead, wrote
 * `L281.3,undefined`, and lost every point after the gap.
 *
 * Both are the same mistake: letting an absent value reach the arithmetic. The
 * fix is to split the series BEFORE scaling anything, via the one shared
 * `segmentSeries`, so no run ever contains a null to scale.
 */
export function linePaths(
    points: readonly TrendPoint[],
    domain: Domain,
    chart: ChartGeometry = CHART,
): string[] {
    return segmentSeries(points, (point) => point.value !== null).map((run) =>
        run
            .map(
                (point, i) =>
                    `${i === 0 ? 'M' : 'L'}${round(scaleX(point.index, points.length, chart))},${round(
                        scaleY(point.value as number, domain, chart),
                    )}`,
            )
            .join(' '),
    );
}

function round(n: number): number {
    return Math.round(n * 10) / 10;
}

// ---------------------------------------------------------------------------
// Change summaries
// ---------------------------------------------------------------------------

export type TrendChange = MetricChange;

/** One metric's story, via the shared `changeIn`. */
export function summarise(points: readonly TrendPoint[], metric: TrendMetric): TrendChange {
    return changeIn(
        points.map((point) => point.value),
        metric.direction,
    );
}

// ---------------------------------------------------------------------------
// Symptom burden
// ---------------------------------------------------------------------------

export const MAX_CONCERN_PER_ROW = 4;

export type BurdenPoint = {
    index: number;
    reportDate: string;
    /** Summed concern across all 19 rows, direction applied. */
    total: number;
    /** The worst it could be, so the total can be shown as a proportion. */
    max: number;
};

/**
 * How much the client's symptoms are asking of them, as one number per report.
 *
 * Every row is converted through `concernOfFrequency`, so the five emotion rows
 * where more is better ("thankful for my life", "content") reduce the burden
 * when they rise instead of adding to it. Summing raw frequency ranks instead
 * would make a client who got more thankful look like a client who got worse.
 */
export function burdenSeries(reports: readonly ProgressReport[]): BurdenPoint[] {
    return reports.map((report, index) => ({
        index,
        reportDate: report.reportDate,
        total: ALL_FREQUENCY_ROWS.reduce(
            (sum, row) => sum + concernOfFrequency(report[row.key], row.direction),
            0,
        ),
        max: ALL_FREQUENCY_ROWS.length * MAX_CONCERN_PER_ROW,
    }));
}

/** The rows that moved most for the better and worst, first report to last. */
export type RowChange = {
    label: string;
    direction: Direction;
    /** Positive means the concern went up, i.e. worse for the client. */
    concernDelta: number;
};

export function rowChanges(reports: readonly ProgressReport[]): RowChange[] {
    if (reports.length < 2) return [];

    const first = reports[0];
    const last = reports[reports.length - 1];

    return ALL_FREQUENCY_ROWS.map((row) => ({
        label: row.label,
        direction: row.direction,
        concernDelta:
            concernOfFrequency(last[row.key], row.direction) -
            concernOfFrequency(first[row.key], row.direction),
    }));
}

export function concernOf(report: ProgressReport, row: FrequencyRow): Concern {
    return concernOfFrequency(report[row.key], row.direction);
}

/**
 * Ordered by the week each report is FOR (#275).
 *
 * A duplicate period can no longer exist — one report per client per week is a
 * unique index — and a client who fills a missed week in late still sorts into
 * the gap rather than onto the end, which ordering by `reportDate` would do.
 */
export { orderReports } from '@light2live/shared-types';

/** "10 Aug", short enough for an x-axis label at phone width. */
export function formatShortDate(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
